import {FC, PropsWithChildren, useState} from 'react'

import {Button} from '@/components/common/content/Button'
import {ButtonSize, ButtonVariant} from '@/components/common/content/ButtonTypes'
import {JurisdictionFlag} from '@/components/common/jurisdiction/JurisdictionFlag'
import {SkeletonBlock} from '@/components/common/skeleton/SkeletonBlock'
import {SkeletonLoadingOverlay} from '@/components/common/skeleton/SkeletonLoadingOverlay'
import {usePreferences} from '@/components/context/Preferences'
import {useProfilePreferences} from '@/components/context/ProfilePreferences'
import {LANGUAGE_SUGGESTION_ANCHOR_ID} from '@/components/i18n/LanguageSuggestion'
import {theme} from '@/theme/theme'
import {localeToJurisdiction} from '@/utils/constants'
import {getCurrencySymbol} from '@/utils/currency'
import {useLanguage} from '@/utils/i18n/language'

import {RegionalSettingsModalLazy} from './RegionalSettingsModalLazy'

// Widest common currency symbol is 3 characters (CHF); the rarer 4-character ones (Mex$) shift
// slightly when they resolve.
const CURRENCY_PLACEHOLDER_WIDTH = '2.5ch'

type RegionalSettingsProps = {
  hasBorder?: boolean
  hasLanguageSuggestion?: boolean
  onClose?: () => void
}

export const RegionalSettings: FC<PropsWithChildren<RegionalSettingsProps>> = ({
  hasBorder = false,
  hasLanguageSuggestion = false,
  onClose,
}) => {
  const language = useLanguage()
  const {currency} = usePreferences()
  const {isCurrencyPending} = useProfilePreferences()
  const [isModalActive, setIsModalActive] = useState(false)
  // A dynamic component fetches as soon as it renders, so the modal must not render before the
  // first click. Latching rather than gating on `isModalActive` keeps the close animation.
  const [hasBeenOpened, setHasBeenOpened] = useState(false)

  const openModal = () => {
    setHasBeenOpened(true)
    setIsModalActive(true)
  }

  return (
    <>
      <div
        className="anchor"
        id={hasLanguageSuggestion ? LANGUAGE_SUGGESTION_ANCHOR_ID : undefined}
      >
        <Button
          size={ButtonSize.medium}
          variant={ButtonVariant.darkGray}
          hasBorder={hasBorder}
          type="button"
          onClick={openModal}
          icon={<JurisdictionFlag jurisdiction={localeToJurisdiction[language]} />}
          iconPosition="left"
          fullWidth
        >
          <span className="language-currency-wrapper">
            {`${language}/`}
            {/* The locale is baked into the URL so it is known even in static markup; the currency
                is not — it comes from the host TLD, the visitor's country, a cookie or, signed in,
                the profile. Reserve roughly the width of the common symbols (€ $ £ Kč CHF) rather
                than rendering a guess that then changes. */}
            {isCurrencyPending ? (
              <SkeletonLoadingOverlay
                highlightColor={theme.palette.skeletonLoading.highlight.onDark}
              >
                <SkeletonBlock
                  width={CURRENCY_PLACEHOLDER_WIDTH}
                  height="0.9em"
                  color={theme.palette.skeletonLoading.dark}
                />
              </SkeletonLoadingOverlay>
            ) : (
              getCurrencySymbol(currency)
            )}
          </span>
        </Button>
      </div>
      {hasBeenOpened && (
        <RegionalSettingsModalLazy
          closeModal={() => {
            setIsModalActive(false)
            onClose?.()
          }}
          isLanguageSelectorVisible
          isVisible={isModalActive}
        />
      )}

      <style jsx>{`
        .anchor {
          position: relative;
        }
        .language-currency-wrapper {
          display: inline-flex;
          align-items: center;
        }
      `}</style>
    </>
  )
}
